/**
 * PostgresConnector — PostgreSQL database resource.
 * Not mountable (but supports snapshot sync to dump schema + sample data).
 * Custom operations: query, schema, execute.
 *
 * Requires `pg` as an optional peer dependency.
 */

import type {
  ConnectContext,
  ConnectorContent,
  ConnectorDeclaration,
  ConnectorEntry,
  ConnectorFieldSpec,
  ConnectorHandle,
  ListOptions,
  OperationDescriptor,
  RuntimeSkillDescriptor,
  SearchOptions,
  SearchResult,
  ToolFace,
} from "@skaile/workspaces/connectors";
import { AbstractConnector } from "@skaile/workspaces/connectors";

interface PgState {
  pool: any; // pg.Pool
  dsn: string;
}

/** Escape an identifier for safe interpolation inside double quotes. */
function quoteIdent(ident: string): string {
  return ident.replace(/"/g, '""');
}

/**
 * Connector for PostgreSQL databases — provides query, schema inspection, and execute operations.
 * Also supports snapshot sync that dumps schema and sample data to JSON files. Requires the `pg` optional peer dependency.
 * @docLink packages/factory-assets/concepts#postgres-connector
 */
export class PostgresConnector extends AbstractConnector {
  readonly name = "postgres";

  tools: ToolFace;

  constructor() {
    super();
    this.tools = {
      read: (handle, path) => this._read(handle, path),
      list: (handle, path, options) => this._list(handle, path, options),
      search: (handle, query, options) => this._search(handle, query, options),
      describeOperations: (handle) => this._describeOperations(handle),
      executeOp: (handle, operation, args) => this._executeOp(handle, operation, args),
      describeSkill: (handle, declaration) => this._describeSkill(handle, declaration),
    };
  }

  describeFields(): ConnectorFieldSpec[] {
    return [
      {
        key: "dsn",
        label: "Connection String",
        description: "PostgreSQL connection URL (e.g. postgres://user:pass@host/db)",
        type: "url",
        required: true,
        sensitive: true,
        group: "Authentication",
      },
    ];
  }

  private _describeSkill(
    _handle: ConnectorHandle,
    declaration: ConnectorDeclaration,
  ): RuntimeSkillDescriptor {
    const dsn = String(declaration.options?.dsn ?? "");
    let hostLabel = declaration.id;
    try {
      hostLabel = new URL(dsn).hostname;
    } catch {}
    return {
      id: declaration.id,
      name: `PostgreSQL — ${hostLabel}`,
      description: `Query PostgreSQL database at ${hostLabel}`,
      category: "database",
      access: declaration.access,
      toolNames:
        declaration.access === "read-write" ? ["query", "schema", "execute"] : ["query", "schema"],
    };
  }

  async connect(declaration: ConnectorDeclaration, ctx: ConnectContext): Promise<ConnectorHandle> {
    const started = Date.now();
    let dsn = ctx.resolvedFields?.dsn ?? String(declaration.options?.dsn ?? "");

    // Backend-mediated: the platform minted the DSN; resolve it via the wrapped
    // mediator (pre-mint initial, real mediator for refresh). Off-disk by design.
    if (!dsn && declaration.auth === "backend" && ctx.tokenMediator) {
      const mint = await ctx.tokenMediator({ connectorId: declaration.id, reason: "initial" });
      if (mint.ok) dsn = mint.token;
      else
        throw new Error(
          `PostgresAdapter '${declaration.id}': backend credential ${mint.code} — ${mint.message}`,
        );
    }

    // Fall back to the secrets chain for env:/pat: refs (non-backend auth).
    if (!dsn) dsn = ctx.secrets?.resolve(declaration.auth ?? "") ?? "";

    if (!dsn) {
      this.log.error("connect failed: missing dsn", undefined);
      throw new Error("PostgresAdapter requires auth (connection string) or options.dsn");
    }

    let host = "(unknown)";
    try {
      host = new URL(dsn).hostname;
    } catch {}
    this.log.info("connect start", { host });

    try {
      const pgPkg = "pg";
      const pg = (await import(/* @vite-ignore */ pgPkg)) as any;
      const Pool = pg.default?.Pool ?? pg.Pool;
      const pool = new Pool({ connectionString: dsn });

      // Verify connectivity
      const client = await pool.connect();
      client.release();

      this.log.info("connect ok", { host, durationMs: Date.now() - started });
      return this.makeHandle(declaration, { pool, dsn } satisfies PgState);
    } catch (err) {
      this.log.error("connect failed", err, { host });
      throw err;
    }
  }

  async disconnect(handle: ConnectorHandle): Promise<void> {
    this.log.info("disconnect");
    const { pool } = handle.state as PgState;
    try {
      await pool.end();
    } finally {
      handle.status = "disconnected";
    }
  }

  private pool(handle: ConnectorHandle): any {
    return (handle.state as PgState).pool;
  }

  // Base ops map to table/row access via path convention: <table>/<pk>

  private async _read(handle: ConnectorHandle, path: string): Promise<ConnectorContent | null> {
    const parts = path.split("/");
    if (parts.length === 1) {
      // Read table schema
      const table = parts[0]!;
      const result = await this.pool(handle).query(
        `SELECT column_name, data_type, is_nullable, column_default
         FROM information_schema.columns WHERE table_name = $1 ORDER BY ordinal_position`,
        [table],
      );
      if (result.rows.length === 0) return null;
      return { data: JSON.stringify(result.rows, null, 2), contentType: "application/json" };
    }
    // Read row by PK: <table>/<pk_value>
    const [table, pk] = parts;
    const result = await this.pool(handle).query(
      `SELECT * FROM "${quoteIdent(table!)}" WHERE id = $1 LIMIT 1`,
      [pk],
    );
    if (result.rows.length === 0) return null;
    return { data: JSON.stringify(result.rows[0], null, 2), contentType: "application/json" };
  }

  private async _list(
    handle: ConnectorHandle,
    path?: string,
    options?: ListOptions,
  ): Promise<ConnectorEntry[]> {
    if (!path) {
      // List tables
      const result = await this.pool(handle).query(
        `SELECT table_name FROM information_schema.tables
         WHERE table_schema = 'public' ORDER BY table_name`,
      );
      return result.rows.map((r: any) => ({
        name: r.table_name,
        path: r.table_name,
        type: "directory" as const,
      }));
    }
    // List rows in a table
    const limit = options?.limit ?? 100;
    const result = await this.pool(handle).query(`SELECT * FROM "${quoteIdent(path)}" LIMIT $1`, [
      limit,
    ]);
    return result.rows.map((row: any, i: number) => ({
      name: String(row.id ?? i),
      path: `${path}/${row.id ?? i}`,
      type: "row" as const,
    }));
  }

  private async _search(
    handle: ConnectorHandle,
    query: string,
    options?: SearchOptions,
  ): Promise<SearchResult[]> {
    // Search across all text columns in all tables
    const tables = await this._list(handle);
    const results: SearchResult[] = [];
    const maxResults = options?.maxResults ?? 50;

    for (const table of tables) {
      if (results.length >= maxResults) break;
      try {
        const cols = await this.pool(handle).query(
          `SELECT column_name FROM information_schema.columns
           WHERE table_name = $1 AND data_type IN ('text', 'character varying', 'varchar')`,
          [table.name],
        );
        if (cols.rows.length === 0) continue;

        const conditions = cols.rows
          .map((c: any) => `"${quoteIdent(String(c.column_name))}"::text ILIKE $1`)
          .join(" OR ");
        const searchResult = await this.pool(handle).query(
          `SELECT * FROM "${quoteIdent(table.name)}" WHERE ${conditions} LIMIT $2`,
          [`%${query}%`, maxResults - results.length],
        );
        for (const row of searchResult.rows) {
          results.push({
            path: `${table.name}/${row.id ?? "?"}`,
            snippet: JSON.stringify(row).slice(0, 200),
          });
        }
      } catch {
        // Skip tables that error (permissions, etc.)
      }
    }
    return results;
  }

  private _describeOperations(handle: ConnectorHandle): OperationDescriptor[] {
    const ops: OperationDescriptor[] = [
      {
        name: "query",
        description: "Execute a read-only SQL query. Returns JSON rows.",
        accessLevel: "read",
        args: [
          { name: "sql", type: "string", required: true, description: "SQL SELECT statement" },
          {
            name: "params",
            type: "json",
            required: false,
            description: "Query parameters as JSON array",
          },
        ],
        examples: [
          'query --sql "SELECT id, name FROM users LIMIT 10"',
          "query --sql \"SELECT * FROM orders WHERE user_id = $1\" --params '[42]'",
        ],
      },
      {
        name: "schema",
        description: "Inspect database schema — tables, columns, types, constraints.",
        accessLevel: "read",
        args: [
          {
            name: "table",
            type: "string",
            required: false,
            description: "Specific table (omit for full schema)",
          },
        ],
        examples: ["schema", "schema --table users"],
      },
    ];

    if (handle.access === "read-write") {
      ops.push({
        name: "execute",
        description: "Execute a SQL mutation (INSERT, UPDATE, DELETE). Returns affected row count.",
        accessLevel: "write",
        args: [
          { name: "sql", type: "string", required: true, description: "SQL statement" },
          {
            name: "params",
            type: "json",
            required: false,
            description: "Query parameters as JSON array",
          },
        ],
        examples: [
          "execute --sql \"UPDATE users SET active = true WHERE id = $1\" --params '[42]'",
        ],
      });
    }

    return ops;
  }

  private async _executeOp(
    handle: ConnectorHandle,
    operation: string,
    args: Record<string, unknown>,
  ): Promise<string> {
    const pool = this.pool(handle);

    switch (operation) {
      case "query":
        return this._opQuery(pool, args);
      case "schema":
        return this._opSchema(pool, args);
      case "execute":
        return this._opExecute(pool, args);
      default:
        throw new Error(`PostgresAdapter has no operation: ${operation}`);
    }
  }

  /** Parse the params argument shared by the query/execute ops (JSON string or passthrough). */
  private parseParams(params: unknown): unknown {
    return params ? (typeof params === "string" ? JSON.parse(params) : params) : [];
  }

  private async _opQuery(pool: any, args: Record<string, unknown>): Promise<string> {
    const sql = String(args.sql ?? args._positional ?? "");
    if (!sql) return "Error: sql argument is required";
    const params = this.parseParams(args.params);
    const queryStart = Date.now();
    try {
      const result = await pool.query(sql, params);
      const ms = Date.now() - queryStart;
      if (ms > 1000) {
        this.log.warn("slow query", { ms, sql: sql.slice(0, 200) });
      }
      return JSON.stringify(result.rows, null, 2);
    } catch (err) {
      this.log.error("query failed", err, { sql: sql.slice(0, 200) });
      throw err;
    }
  }

  private async _opSchema(pool: any, args: Record<string, unknown>): Promise<string> {
    const table = args.table ? String(args.table) : undefined;
    if (table) {
      const result = await pool.query(
        `SELECT column_name, data_type, is_nullable, column_default
         FROM information_schema.columns WHERE table_name = $1 ORDER BY ordinal_position`,
        [table],
      );
      return JSON.stringify(result.rows, null, 2);
    }
    // Full schema: tables + row counts
    const tables = await pool.query(
      `SELECT t.table_name,
              (SELECT count(*) FROM information_schema.columns c WHERE c.table_name = t.table_name) as column_count
       FROM information_schema.tables t
       WHERE t.table_schema = 'public' ORDER BY t.table_name`,
    );
    return JSON.stringify(tables.rows, null, 2);
  }

  private async _opExecute(pool: any, args: Record<string, unknown>): Promise<string> {
    const sql = String(args.sql ?? args._positional ?? "");
    if (!sql) return "Error: sql argument is required";
    const params = this.parseParams(args.params);
    const result = await pool.query(sql, params);
    return JSON.stringify({ rowCount: result.rowCount, command: result.command });
  }
}

/**
 * Creates a new PostgresConnector instance.
 * @returns Configured PostgresConnector ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#postgres-connector-factory
 */
export function createConnector(): PostgresConnector {
  return new PostgresConnector();
}
